import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Globe } from "lucide-react";
import { useT, LANG_LIST } from "@/lib/i18n";

export default function LanguageSwitcher({ compact = false }) {
    const { lang, t, setLang } = useT();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = LANG_LIST.find((l) => l.code === lang) || LANG_LIST[0];

    useEffect(() => {
        const onDown = (e) => {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) setOpen(false);
        };
        window.addEventListener("mousedown", onDown);
        window.addEventListener("touchstart", onDown);
        return () => {
            window.removeEventListener("mousedown", onDown);
            window.removeEventListener("touchstart", onDown);
        };
    }, []);

    return (
        <div ref={ref} className="relative" data-testid="language-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={t.languageSwitcher.aria}
                aria-expanded={open}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/12 text-white/85 hover:text-white hover:border-white/25 transition-colors text-sm font-medium"
                data-testid="language-switcher-toggle"
            >
                <Globe size={14} strokeWidth={1.75} className="opacity-75" />
                <span className="hidden sm:inline">{current.short}</span>
                <span className="sm:hidden">{current.flag}</span>
                <ChevronDown
                    size={14}
                    className={`opacity-70 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            {open && (
                <div
                    role="listbox"
                    className={`absolute right-0 mt-2 w-44 rounded-md border border-white/12 bg-[color:var(--bg-base)]/95 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] overflow-hidden z-50 ${
                        compact ? "" : ""
                    }`}
                    data-testid="language-switcher-menu"
                >
                    {LANG_LIST.map((l) => {
                        const active = l.code === lang;
                        return (
                            <button
                                key={l.code}
                                type="button"
                                role="option"
                                aria-selected={active}
                                onClick={() => {
                                    setLang(l.code);
                                    setOpen(false);
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                                    active
                                        ? "text-white bg-white/[0.06]"
                                        : "text-white/75 hover:text-white hover:bg-white/[0.04]"
                                }`}
                                data-testid={`language-option-${l.code}`}
                            >
                                <span className="text-base" aria-hidden="true">
                                    {l.flag}
                                </span>
                                <span className="flex-1">{l.label}</span>
                                {active && (
                                    <Check
                                        size={14}
                                        strokeWidth={2}
                                        className="text-[color:var(--accent)]"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
